import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { debounce } from "@mui/material";
import axios from "@helpers/axios/private.axios";
import { getAllTickers, subscribeTickers } from "@actions/futures/futureData";
import {
  BINANCE_STREAM_HOST,
  isBinanceStreamLive,
} from "@helpers/binance/binanceStreams";
import { FUTURES } from "../../../../../../../constants";
import { switchFuturesPair } from "../../switchFuturesPair";

const TICKERS_FALLBACK_POLL_MS = 5000;
const SEARCH_DEBOUNCE_MS = 500;

const sameSlug = (left, right) =>
  String(left || "").toUpperCase() === String(right || "").toUpperCase();

/**
 * Pairs list, search, URL ↔ redux pair sync and live tickers while open.
 *
 * @param {object} params
 * @param {boolean} params.isOpen
 */
export const useFuturesPairs = ({ isOpen }) => {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const wasOpenRef = useRef(false);

  const dispatch = useDispatch();
  const currentPair = useSelector((state) => state.futures.pair);

  // Fetches pairs (without prices). On the first load also applies the pair
  // from query params; later reloads (after close / clearing the search) only
  // refresh the list, otherwise a stale URL could switch the pair back.
  const loadPairs = async ({ applyUrlPair = false } = {}) => {
    try {
      setLoading(true);

      const { data } = await axios.get("/futures-pairs");
      const list = data.data;

      setPairs(list);

      if (!applyUrlPair) return;

      const pairFromUrl = searchParams.get("pair");
      const foundPair = pairFromUrl
        ? list.find((pair) => sameSlug(pair.slug, pairFromUrl))
        : null;

      if (foundPair) {
        dispatch({ type: FUTURES.CHANGE_PAIR, payload: foundPair });
        return;
      }

      // The default/saved pair has no backend `id`: take it from the list by slug
      const currentFromList = list.find((pair) =>
        sameSlug(pair.slug, currentPair?.slug),
      );

      if (currentFromList) {
        // A pair saved in the browser can miss fields the backend added later
        if (
          currentFromList.id !== currentPair.id ||
          currentFromList.open_fee_percent !== currentPair.open_fee_percent
        ) {
          dispatch({ type: FUTURES.SYNC_PAIR, payload: currentFromList });
        }
        return;
      }

      if (list.length > 0) {
        dispatch({ type: FUTURES.CHANGE_PAIR, payload: list[0] });
        setSearchParams({ pair: list[0].slug });
      }
    } catch (error) {
      setPairs([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search always calls the latest loadPairs
  const loadPairsRef = useRef(loadPairs);
  loadPairsRef.current = loadPairs;

  const searchPairs = useMemo(
    () =>
      debounce(async (name) => {
        if (name.trim() === "") {
          loadPairsRef.current();
          return;
        }

        try {
          const { data } = await axios.post("/futures-pairs/search", { name });
          setPairs(data.data);
        } catch (error) {
          setPairs([]);
        } finally {
          setLoading(false);
        }
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => searchPairs.clear(), [searchPairs]);

  useEffect(() => {
    loadPairs({ applyUrlPair: true });
    dispatch(getAllTickers());
  }, []);

  // Full snapshot on open, then the stream; the poll runs only while the stream is down
  useEffect(() => {
    if (!isOpen) return undefined;

    dispatch(getAllTickers());
    const interval = setInterval(() => {
      if (!isBinanceStreamLive(BINANCE_STREAM_HOST.MARKET)) {
        dispatch(getAllTickers());
      }
    }, TICKERS_FALLBACK_POLL_MS);

    return () => clearInterval(interval);
  }, [isOpen, dispatch]);

  const pairSlugsKey = pairs.map(({ slug }) => slug).join(",");

  useEffect(() => {
    if (!isOpen || !pairSlugsKey) return undefined;

    return dispatch(subscribeTickers(pairSlugsKey.split(",")));
  }, [isOpen, dispatch, pairSlugsKey]);

  // Reset search after the modal closes
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setSearch("");
      loadPairs();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const onSearch = (value) => {
    setSearch(value);
    setLoading(true);
    searchPairs(value);
  };

  const selectPair = (nextPair) => {
    const currentFromList = pairs.find((pair) =>
      sameSlug(pair.slug, currentPair?.slug),
    );

    switchFuturesPair({
      dispatch,
      setSearchParams,
      currentPair: currentFromList || currentPair,
      nextPair,
    });
  };

  const filteredPairs = pairs.filter((pair) =>
    pair.name.toLowerCase().includes(search.toLowerCase()),
  );

  return {
    currentPair,
    pairs: filteredPairs,
    loading,
    search,
    onSearch,
    selectPair,
  };
};
